import { MonitorStatus } from '@fe/core';
import { SvgIcon } from '@progress/kendo-react-common';
import clsx from 'clsx';
import * as React from 'react';
import {
    AirportConfig,
    AirportDeicing,
    AirportDelay,
    AirportDiversionManagementInfo,
    AirportHoldingInfo,
    AirportMetar,
    AirportRvr,
    AirportTaf,
    WindDirection
} from '../../../airports/airport';
import { AirportClosureType, AirportFlightCategory, AirportSelection, VisibilityRelation } from '../../../common/enums';
import { DateTimeFormatter } from '../../../formatters';
import { BorderColorOutline } from '../../../icons/icons';
import { MonitorStatusBadge } from '../../components/monitor-status/MonitorStatusBadge';
import { FlightCategoryBadge, TafBar } from './TafBar';

export interface AirportFieldData {
    iataId: string | null;
    icaoId: string | null;
    metar: AirportMetar;
    taf: AirportTaf;
    rvr: AirportRvr;
    delay: AirportDelay;
    config: AirportConfig;
    deicing: AirportDeicing;
    diversion: AirportDiversionManagementInfo;
    holding: AirportHoldingInfo;
    hasNotes: boolean;
    thresholds: {
        monitorStatus: MonitorStatus;
        origin: { monitorStatus: MonitorStatus } | null;
        destination: { monitorStatus: MonitorStatus } | null;
        alternate: { monitorStatus: MonitorStatus } | null;
    } | null;
}

const rvrColorClass = (value: number) => {
    if (value >= 60) {
        return 1;
    }
    if (value >= 24) {
        return 2;
    }
    if (value >= 12) {
        return 3;
    }
    if (value >= 6) {
        return 4;
    }
    if (value >= 3) {
        return 5;
    }
    return 6;
};

const WindDirectionData = (value: WindDirection) => {
    if (typeof value === 'number') {
        const direction: number = value / 45;
        const rounded: number = Math.round(direction);
        if (rounded >= 8) {
            return 'N';
        }
        if (rounded >= 7) {
            return 'NW';
        }
        if (rounded >= 6) {
            return 'W';
        }
        if (rounded >= 5) {
            return 'SW';
        }
        if (rounded >= 4) {
            return 'S';
        }
        if (rounded >= 3) {
            return 'SE';
        }
        if (rounded >= 2) {
            return 'E';
        }
        if (rounded >= 1) {
            return 'NE';
        }
        if (rounded >= 0) {
            return 'N';
        }
    } else if (value === 'variable') {
        return value;
    } else {
        return '';
    }
};

export function getFormattedAirportField(airport: AirportFieldData, field: AirportSelection): React.ReactNode | null {
    switch (field) {
        case AirportSelection.None:
            return null;
        case AirportSelection.ArrRate:
            return airport.config.arrivalRate == null ? '' : airport.config.arrivalRate.toString();
        case AirportSelection.ArrRWays:
            return airport.config.arrivalRunway == null ? '' : airport.config.arrivalRunway;
        case AirportSelection.arrDel: {
            const delay = airport.delay;
            const item = delay.items.filter((x) => x.type === AirportClosureType.arrival).at(0);
            return item ? <>Avg: {item.delayMinutesMinAverage} min</> : <>-</>;
        }
        case AirportSelection.closureAltPS:
            return airport.diversion.alternateCountPS == null ? '' : airport.diversion.alternateCountPS.toString();
        case AirportSelection.closureAltAct:
            return airport.diversion.alternateCountActive == null ? '' : airport.diversion.alternateCountActive.toString();
        case AirportSelection.closuredm: {
            switch (airport.diversion.closure) {
                case 0:
                    return 'None';
                case 1:
                    return 'Airline';
                case 2:
                    return 'Airport';
                default:
                    return 'None';
            }
        }
        case AirportSelection.closurestart:
            return DateTimeFormatter.formatTime(airport.diversion.closeStart);
        case AirportSelection.closureend:
            return DateTimeFormatter.formatTime(airport.diversion.closeEnd);
        case AirportSelection.closureDestPS:
            return airport.diversion.destinationCountPS == null ? '' : airport.diversion.destinationCountPS.toString();
        case AirportSelection.closureDestAct:
            return airport.diversion.destinationCountActive == null ? '' : airport.diversion.destinationCountActive.toString();
        case AirportSelection.depDel: {
            const delay = airport.delay;
            const item = delay.items.filter((x) => x.type === AirportClosureType.departure).at(0);
            return item ? (
                <>
                    min/max: {item.delayMinutesMinAverage}/{item.delayMinutesMax} mins
                </>
            ) : (
                <></>
            );
        }
        case AirportSelection.DepRate:
            return airport.config.departureRate == null ? '' : airport.config.departureRate.toString();
        case AirportSelection.Deicing: {
            switch (airport.deicing.status) {
                case 0:
                    return 'Not Active';
                case 1:
                    return 'Not Active';
                case 2:
                    return 'Active';
                default:
                    return 'None';
            }
        }
        case AirportSelection.DepRWays:
            return airport.config.departureRunway == null ? '' : airport.config.departureRunway;
        case AirportSelection.metarFc:
            return airport.metar.flightCategory === AirportFlightCategory.unknown
                ? ''
                : <FlightCategoryBadge category={airport.metar.flightCategory} />;
        case AirportSelection.metarVis: {
            const metar = airport.metar;
            return (
                <>
                    {metar.visibility.relation === VisibilityRelation.greaterThan && '>'}
                    {metar.visibility.relation === VisibilityRelation.lessThan && '<'}
                    {metar.visibility.value != null && `${metar.visibility.value.toFixed(2)}SM`}
                </>
            );
        }
        case AirportSelection.metarCeil: {
            const metar = airport.metar;
            return <>{metar.ceiling != null && `${metar.ceiling.toLocaleString('en-US')} ft`}</>;
        }
        case AirportSelection.metarWs: {
            const metar = airport.metar;
            return <>{metar.windSpeed != null && `${metar.windSpeed} kts`}</>;
        }
        case AirportSelection.metarWg: {
            const metar = airport.metar;
            return <>{metar.windGusts != null && `${metar.windGusts} kts`}</>;
        }
        case AirportSelection.metarTmp: {
            const metar = airport.metar;
            return <>{metar.temperature != null && `${metar.temperature}°C`}</>;
        }
        case AirportSelection.metarWd: {
            const metar = airport.metar;
            return <>{metar.windDirection != null && `${WindDirectionData(metar.windDirection)}`}</>;
        }
        case AirportSelection.metarDew: {
            const metar = airport.metar;
            return <>{metar.dewPoint != null && `${metar.dewPoint}°C`}</>;
        }
        case AirportSelection.metarAltimeter: {
            const metar = airport.metar;
            return <>{metar.temperature != null && `${metar.altimeter} in.`}</>;
        }
        case AirportSelection.metartime: {
            return DateTimeFormatter.formatTime(airport.metar.receivedTime);
        }
        case AirportSelection.taftime: {
            return DateTimeFormatter.formatTime(airport.taf.receivedTime);
        }
        case AirportSelection.taf: {
            return <TafBar tafs={airport.taf} />;
        }
        case AirportSelection.rvrWc: {
            const rvr = airport.rvr;
            let content = '';
            let className = '';
            if (rvr.worstCaseVisibility === 88) {
                content = 'No Report';
            } else if (rvr.worstCaseVisibility != null) {
                className = 'rvr-' + rvrColorClass(rvr.worstCaseVisibility);
                content = (rvr.worstCaseVisibility * 100).toString();
            }
            return <div className={clsx('text-center', className)}>{content}</div>;
        }
        case AirportSelection.gndStp: {
            const delay = airport.delay;
            const item = delay.items.filter((x) => x.type === AirportClosureType.groundStop).at(0);
            const text = DateTimeFormatter.formatDateTime(item?.stopTime);
            return <>{text && 'Until:' + text}</>;
        }
        case AirportSelection.gndDelay: {
            const delay = airport.delay;
            const item = delay.items.filter((x) => x.type === AirportClosureType.groundDelay).at(0);
            return item ? <>Avg: {item.delayMinutesMinAverage}min</> : <></>;
        }
        case AirportSelection.clFaa: {
            const delay = airport.delay;
            const item = delay.items.filter((x) => x.type === AirportClosureType.closure).at(0);
            return (
                <>
                    {item && (
                        <>
                            Closed: {DateTimeFormatter.formatDateTime(item.startTime)}
                            {item.startTime && ' - '}
                            {DateTimeFormatter.formatDateTime(item.stopTime)}
                        </>
                    )}
                </>
            );
        }
        case AirportSelection.iataId:
            return airport.iataId == null ? '' : airport.iataId;
        case AirportSelection.icaoId:
            return airport.icaoId == null ? '' : airport.icaoId;
        case AirportSelection.diversioncapacityairlinenarrow:
            return airport.diversion.capacity.airline.narrow == null ? '' : airport.diversion.capacity.airline.narrow.toString();
        case AirportSelection.diversioncapacityairlinewide:
            return airport.diversion.capacity.airline.wide == null ? '' : airport.diversion.capacity.airline.wide.toString();
        case AirportSelection.diversioncapacitynetworknarrow:
            return airport.diversion.capacity.network.narrow == null ? '' : airport.diversion.capacity.network.narrow.toString();
        case AirportSelection.diversioncapacitynetworkwide:
            return airport.diversion.capacity.network.wide == null ? '' : airport.diversion.capacity.network.wide.toString();
        case AirportSelection.diversioncapacityothernarrow:
            return airport.diversion.capacity.other.narrow == null ? '' : airport.diversion.capacity.other.narrow.toString();
        case AirportSelection.diversioncapacityotherwide:
            return airport.diversion.capacity.other.wide == null ? '' : airport.diversion.capacity.other.wide.toString();
        case AirportSelection.diversiondivertairlinenarrow:
            return airport.diversion.divert.airline.narrow == null ? '' : airport.diversion.divert.airline.narrow.toString();
        case AirportSelection.diversiondivertairlinewide:
            return airport.diversion.divert.airline.wide == null ? '' : airport.diversion.divert.airline.wide.toString();
        case AirportSelection.diversiondivertnetworknarrow:
            return airport.diversion.divert.network.narrow == null ? '' : airport.diversion.divert.network.narrow.toString();
        case AirportSelection.diversiondivertnetworkwide:
            return airport.diversion.divert.network.wide == null ? '' : airport.diversion.divert.network.wide.toString();
        case AirportSelection.diversiondivertothernarrow:
            return airport.diversion.divert.other.narrow == null ? '' : airport.diversion.divert.other.narrow.toString();
        case AirportSelection.diversiondivertotherwide:
            return airport.diversion.divert.other.wide == null ? '' : airport.diversion.divert.other.wide.toString();
        case AirportSelection.diversionavailableairlinenarrow:
            return airport.diversion.available.airline.narrow == null ? '' : airport.diversion.available.airline.narrow.toString();
        case AirportSelection.diversionavailableairlinewide:
            return airport.diversion.available.airline.wide == null ? '' : airport.diversion.available.airline.wide.toString();
        case AirportSelection.diversionavailablenetworknarrow:
            return airport.diversion.available.network.narrow == null ? '' : airport.diversion.available.network.narrow.toString();
        case AirportSelection.diversionavailablenetworkwide:
            return airport.diversion.available.network.wide == null ? '' : airport.diversion.available.network.wide.toString();
        case AirportSelection.diversionavailableothernarrow:
            return airport.diversion.available.other.narrow == null ? '' : airport.diversion.available.other.narrow.toString();
        case AirportSelection.diversionavailableotherwide:
            return airport.diversion.available.other.wide == null ? '' : airport.diversion.available.other.wide.toString();
        case AirportSelection.diversioncomments:
            return airport.diversion.comments == null ? '' : airport.diversion.comments;
        case AirportSelection.diversioncapacityairlinetotal:
            return airport.diversion.capacity.airline.total == null ? '' : airport.diversion.capacity.airline.total.toString();
        case AirportSelection.diversioncapacitynetworktotal:
            return airport.diversion.capacity.network.total == null ? '' : airport.diversion.capacity.network.total.toString();
        case AirportSelection.diversioncapacityothertotal:
            return airport.diversion.capacity.other.total == null ? '' : airport.diversion.capacity.other.total.toString();
        case AirportSelection.diversiondivertairlinetotal:
            return airport.diversion.divert.airline.total == null ? '' : airport.diversion.divert.airline.total.toString();
        case AirportSelection.diversiondivertnetworktotal:
            return airport.diversion.divert.network.total == null ? '' : airport.diversion.divert.network.total.toString();
        case AirportSelection.diversiondivertothertotal:
            return airport.diversion.divert.other.total == null ? '' : airport.diversion.divert.other.total.toString();
        case AirportSelection.diversionavailableairlinetotal:
            return airport.diversion.available.airline.total == null ? '' : airport.diversion.available.airline.total.toString();
        case AirportSelection.diversionavailablenetworktotal:
            return airport.diversion.available.network.total == null ? '' : airport.diversion.available.network.total.toString();
        case AirportSelection.diversionavailableothertotal:
            return airport.diversion.available.other.total == null ? '' : airport.diversion.available.other.total.toString();
        case AirportSelection.MonStatApt:
            return (
                <div className="d-flex">
                    <MonitorStatusBadge monitorStatus={airport.thresholds?.monitorStatus} />
                </div>
            );
        case AirportSelection.MonStatOrg:
            return (
                <div className="d-flex">
                    <MonitorStatusBadge monitorStatus={airport.thresholds?.origin?.monitorStatus} />
                </div>
            );
        case AirportSelection.MonStatDst:
            return (
                <div className="d-flex">
                    <MonitorStatusBadge monitorStatus={airport.thresholds?.destination?.monitorStatus} />
                </div>
            );
        case AirportSelection.MonStatAlt:
            return (
                <div className="d-flex">
                    <MonitorStatusBadge monitorStatus={airport.thresholds?.alternate?.monitorStatus} />
                </div>
            );
        case AirportSelection.fltholdinginto:
            return airport.holding.items.length;
        case AirportSelection.Notes: {
            return airport.hasNotes ? <SvgIcon icon={BorderColorOutline} /> : <></>;
        }
    }
}
